import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';

import * as LoyaltyService from '~/services/LoyaltyService';

import type {
  UpdatePointsPurchaseOfferCampaignRequestBody,
  UpdatePointsPurchaseOfferRequestBody,
} from '~/types/loyaltyContract';

import { pointsPurchaseQueryKeys } from './pointsPurchaseQueryKeys';

export function useCreatePointsPurchaseOffer() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  return useMutation({
    mutationFn: LoyaltyService.createPointsPurchaseOffer,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: pointsPurchaseQueryKeys.offers() });
      enqueueSnackbar('Offer created successfully', { variant: 'success' });
    },
    onError: (error: Error) => {
      enqueueSnackbar(`Failed to create offer: ${error.message ?? 'Unknown error'}`, { variant: 'error' });
    },
  });
}

export function useUpdatePointsPurchaseOffer() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  return useMutation({
    mutationFn: ({ offerId, params }: { offerId: string; params: UpdatePointsPurchaseOfferRequestBody }) =>
      LoyaltyService.updatePointsPurchaseOffer(offerId, params),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: pointsPurchaseQueryKeys.offers() });
      enqueueSnackbar('Offer updated successfully', { variant: 'success' });
    },
    onError: (error: Error) => {
      enqueueSnackbar(`Failed to update offer: ${error.message ?? 'Unknown error'}`, { variant: 'error' });
    },
  });
}

export function useCreatePointsPurchaseOfferCampaign() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  return useMutation({
    mutationFn: LoyaltyService.createPointsPurchaseOfferCampaign,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: pointsPurchaseQueryKeys.campaigns() });
      await queryClient.invalidateQueries({ queryKey: pointsPurchaseQueryKeys.offers() });
      enqueueSnackbar('Campaign created successfully', { variant: 'success' });
    },
    onError: (error: Error) => {
      enqueueSnackbar(`Failed to create campaign: ${error.message ?? 'Unknown error'}`, { variant: 'error' });
    },
  });
}

export function useUpdatePointsPurchaseOfferCampaign() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  return useMutation({
    mutationFn: ({
      campaignId,
      params,
    }: {
      campaignId: string;
      params: UpdatePointsPurchaseOfferCampaignRequestBody;
    }) => LoyaltyService.updatePointsPurchaseOfferCampaign(campaignId, params),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: pointsPurchaseQueryKeys.campaigns() });
      await queryClient.invalidateQueries({ queryKey: pointsPurchaseQueryKeys.offers() });
      enqueueSnackbar('Campaign updated successfully', { variant: 'success' });
    },
    onError: (error: Error) => {
      enqueueSnackbar(`Failed to update campaign: ${error.message ?? 'Unknown error'}`, { variant: 'error' });
    },
  });
}
